import { Api, ApiRequest } from "laravel-request";
import { Service } from "#app/components/Funnel/actives/reports/types";
import {
    ClientDict,
    CompressedCrmReport,
    DecompressedCrmReport,
    DetailedCell,
    DetailedTable,
    MonthKey,
    ServiceAton,
    ServiceAtonStatus,
    ServiceDict,
    ServiceRow,
    ServicesAggregated,
    ServiceTable,
    TotalRow,
    TotalsTable,
} from "#app/api/types";
import { downloadExcelFile } from "#app/helpers/urlHelper";

/**
 * Ручка для смены статуса "оплачено" у услуги
 * @param {any[] | number} rows сущности, подлежащие обновлению. Можно пропустить как массив, так и отдельную запись
 * @param callbackOnPaidChange
 */
export const crmServicesPutPaid = (
    rows: any[] | any,
    callbackOnPaidChange: (data: unknown) => void
) => {
    const entries = Array.isArray(rows) ? rows : [rows];
    const warnInConsole = (entry: unknown) => console.warn("Отсутствует id у записи", entry);

    const allRecords = entries.reduce((accumulator, row) => {
        if (row.dataEntries && Array.isArray(row.dataEntries)) {
            accumulator.push(...row.dataEntries);
        } else {
            accumulator.push(row);
        }
        return accumulator;
    }, []);

    const ids: number[] = [];
    const isPaidMap: Record<number, unknown> = {};

    for (const record of allRecords) {
        const id = record.id ?? record.data?.id;
        if (!id) {
            warnInConsole(record);
            continue;
        }

        const isPaid = record.is_paid ?? record.data?.is_paid;
        ids.push(id);
        isPaidMap[id] = isPaid;
    }

    if (ids.length === 0) {
        console.warn("Нет записей для обновления");
        return Promise.resolve();
    }

    const allRowsPaid = Object.values(isPaidMap).every(Boolean);
    let payload = { is_paid: allRowsPaid ? 1 : 0 };
    if (ids.length > 1) {
        payload = { ...payload, ...{ ids: ids } };
    }

    return Api.putArg("crm-services", "paid", ids[0], payload).call(() => {
        if (typeof callbackOnPaidChange === "function") {
            // @ts-ignore // @TODO сделать здесь тип
            callbackOnPaidChange(payload);
        }
    });
};

/**
 * Ручка для привязки клиенту
 * @param valuationsList
 * @param apiParams
 * @param callbackOnAssign
 */
export const assignServices = (
    valuationsList: unknown[] | null,
    apiParams: Record<string, unknown>,
    callbackOnAssign?: () => void
) =>
    Api.post(
        "crm-services",
        "assign",
        valuationsList ? { ...apiParams, ...{ valuations: valuationsList } } : apiParams
    ).call(() => callbackOnAssign && callbackOnAssign());

export const calculateSuccessFee = (clientId: number, callbackOnSuccess?: () => void) =>
    Api.post("crm-services", "calculate-success", {
        client_id: clientId,
    }).call(() => callbackOnSuccess && callbackOnSuccess());

/**
 * Ручка для получения всех сервисов по определенному клиенту
 * @param clientId
 * @param callbackOnGetServices
 */
export const getServices = (clientId: number, callbackOnGetServices: (data: unknown) => void) => {
    Api.get("crm-services", "index")
        .with(
            "clients",
            (query: {
                orderBy: (s: string) => unknown;
                where: (a: string, b: string, c: number) => unknown;
            }) => {
                query.orderBy("created_at");
                return query.where("client_id", "=", clientId);
            }
        )
        .all((response) => callbackOnGetServices(response.data));
};

export const getAllServices = (
    callback: (data: Service[]) => void,
    isArchived: boolean | null = null
) => {
    const base = Api.get("crm-services", "index");
    const withClients = base.with("clients", (relQuery: unknown) => {
        type RelationBuilder = {
            with: (a: string, b?: (cb: RelationBuilder) => RelationBuilder) => RelationBuilder;
        };
        const q = relQuery as RelationBuilder;
        return q
            .with("client", (clientQuery) => clientQuery.with("mainContact"))
            .with("payments", (innerQuery) => innerQuery.with("tariff"));
    });
    const query = withClients as ApiRequest;
    const finalQuery = isArchived !== null ? query.where("is_archived", isArchived) : query;
    finalQuery.all((response: { data: Service[] }) => callback(response.data));
};

export const getAllServicesCompressed = (
    userId: any,
    callback: (data: any) => void,
    isArchived: boolean | null = null
) => {
    Api.get("crm-services", "table-report", {
        is_archived: isArchived,
        responsible_id: userId,
    }).all((response) => {
        const decompressedResponse = decompressCrmReport(response.data);
        callback(decompressedResponse);
    });
};

export const setServiceToUpdate = (
    id: number,
    isArchived: boolean,
    callbackOnSucces: (arg0: any) => any
) => {
    Api.putArg("crm-services", "update", id, { is_archived: isArchived }).call((response) =>
        callbackOnSucces(response)
    );
};

export const generateExcelFileByServiceData = (
    data: any,
    clientName: string,
    serviceName: string,
    callbackOnSuccess: (arg0: any) => any
) => {
    Api.post("crm-services", "generate-excel", {
        data: data,
        client: clientName,
        service: serviceName,
    }).call((response) => callbackOnSuccess && callbackOnSuccess(response));
};

export const decompressCrmReport = (
    compressed: CompressedCrmReport
): DecompressedCrmReport & {
    tables: unknown;
    clientDirectory: Record<number, { id: number; first_name: string; last_name: string }>;
} => {
    const isArray = Array.isArray; // алиас, чтобы не дублировать каждый раз
    const convertValueToACertainString = (numberOrString: string | number): number => {
        const value =
            typeof numberOrString === "number" ? numberOrString : parseInt(numberOrString, 10);
        return Number.isFinite(value) ? value : 0;
    };

    const coerceRow = (row: unknown): ServiceRow | TotalRow => {
        if (!isArray(row)) return [0, 0, 0, []];
        const [amount, isPaid, isArchived, ids] = row as any[];
        return [
            typeof amount === "number" ? amount : 0,
            typeof isPaid === "number" ? isPaid : Number(Boolean(isPaid)),
            typeof isArchived === "number" ? isArchived : Number(Boolean(isArchived)),
            isArray(ids) ? ids : [],
        ];
    };

    const createEmptyCell = (): DetailedCell => ({
        amount: 0,
        is_paid: false,
        is_archived: false,
        ids: [],
        is_paid_list: [],
        is_archived_list: [],
    });

    const monthKeysDesc: MonthKey[] = isArray(compressed.m) ? (compressed.m as MonthKey[]) : [];
    const clientDict: ClientDict = (compressed.c ?? {}) as ClientDict;
    const serviceDict: ServiceDict = (compressed.s ?? {}) as ServiceDict;
    const activeClientIds: number[] = (compressed.a ?? []).map(convertValueToACertainString);
    const archivedClientIds: number[] = (compressed.r ?? []).map(convertValueToACertainString);
    const totalTable: TotalsTable = (compressed.t ?? {}) as TotalsTable;
    const serviceTable: ServiceTable = (compressed.v ?? {}) as ServiceTable;
    const serviceAton: ServiceAton = (compressed.va ?? {}) as ServiceAton;
    const serviceAtonStatus: ServiceAtonStatus = (compressed.vas ?? {}) as ServiceAtonStatus;

    const clientDirectory: Record<number, { id: number; first_name: string; last_name: string }> =
        {};

    for (const [idStringified, valuesList] of Object.entries(clientDict)) {
        const clientId = convertValueToACertainString(idStringified);
        const first = isArray(valuesList) && typeof valuesList[0] === "string" ? valuesList[0] : "";
        const last = isArray(valuesList) && typeof valuesList[1] === "string" ? valuesList[1] : "";
        clientDirectory[clientId] = { id: clientId, first_name: first, last_name: last };
    }

    const archivedByClientId: Record<number, boolean> = {};
    for (const idStringified of Object.keys(clientDict)) {
        const id = convertValueToACertainString(idStringified);
        archivedByClientId[id] = archivedClientIds.includes(id);
    }

    const buildDetailedTableForClients = (
        clients: Array<{ id: number; first_name: string; last_name: string }>
    ): DetailedTable => {
        const table: DetailedTable = {};

        clients.forEach((client) => {
            const clientId = client.id;
            const printableName =
                (client.last_name.trim() + " " + client.first_name.trim()).trim() ||
                `Клиент ${clientId}`;
            const totalCellsByMonth: Record<string, DetailedCell> = {};
            for (const monthKey of monthKeysDesc) totalCellsByMonth[monthKey] = createEmptyCell();

            const clientTotalData = (totalTable[String(clientId)] ??
                totalTable[clientId] ??
                []) as unknown[];

            for (let i = 0; i < monthKeysDesc.length; i++) {
                const monthKey = monthKeysDesc[i];
                const [amount, isPaid, isArchived, ids] = coerceRow(clientTotalData[i]);
                const cell = totalCellsByMonth[monthKey];
                cell.amount = amount;
                cell.is_paid = Boolean(isPaid);
                cell.is_archived = Boolean(isArchived);
                cell.ids = (ids as Array<number | string>).map(convertValueToACertainString);
                cell.is_paid_list = [Boolean(isPaid)];
                cell.is_archived_list = [Boolean(isArchived)];
            }

            const servicesAggregated: ServicesAggregated = {};
            const clientServices = (serviceTable[String(clientId)] ??
                serviceTable[clientId] ??
                {}) as Record<string, unknown>;

            for (const [serviceIdStringified, monthlyArrayUnknown] of Object.entries(
                clientServices
            )) {
                const serviceId = convertValueToACertainString(serviceIdStringified);
                const serviceName =
                    (serviceDict as any)[String(serviceId)] ??
                    (serviceDict as any)[serviceId] ??
                    `Услуга ${serviceId}`;

                const monthlyCellsByService: Record<string, DetailedCell> = {};
                for (const monthKeyEntry of monthKeysDesc)
                    monthlyCellsByService[monthKeyEntry] = createEmptyCell();

                const monthlyArray = (monthlyArrayUnknown as unknown[]) ?? [];
                monthKeysDesc.forEach((monthKeyEntry, index) => {
                    const cell = monthlyCellsByService[monthKeyEntry];
                    const [amount, isPaid, isArchived, ids] = coerceRow(monthlyArray[index]);
                    cell.amount = amount;
                    cell.is_paid = Boolean(isPaid);
                    cell.is_archived = Boolean(isArchived);
                    cell.ids = (ids as Array<number | string>).map(convertValueToACertainString);
                });

                const atonAccountsRaw =
                    (serviceAton[String(clientId)] &&
                        serviceAton[String(clientId)][String(serviceId)]) ??
                    (serviceAton[clientId] && serviceAton[clientId][serviceId]) ??
                    [];

                const aton_accounts = (atonAccountsRaw as Array<number | string>).map(
                    convertValueToACertainString
                );

                const accountArchivedStatusRaw =
                    (serviceAtonStatus[String(clientId)] &&
                        serviceAtonStatus[String(clientId)][String(serviceId)]) ??
                    (serviceAtonStatus[clientId] && serviceAtonStatus[clientId][serviceId]) ??
                    {};

                const accountArchivedStatus: Record<number, boolean> = {};
                for (const [atonIdStringified, flag] of Object.entries(
                    accountArchivedStatusRaw as Record<string, unknown>
                )) {
                    accountArchivedStatus[convertValueToACertainString(atonIdStringified)] =
                        Boolean(flag as any);
                }

                servicesAggregated[serviceName] = {
                    ...monthlyCellsByService,
                    aton_accounts,
                    accountArchivedStatus,
                    serviceId,
                } as ServicesAggregated[string];
            }

            table[printableName] = {
                client: { id: clientId },
                total: totalCellsByMonth,
                services: servicesAggregated,
            };
        });

        return table;
    };

    const clientsSortedByName = Object.values(clientDirectory).sort((firstClient, secondClient) => {
        const firstClientName =
            (firstClient.last_name + " " + firstClient.first_name).trim() ||
            `Клиент ${firstClient.id}`;
        const secondClientName =
            (secondClient.last_name + " " + secondClient.first_name).trim() ||
            `Клиент ${secondClient.id}`;
        return firstClientName.localeCompare(secondClientName, "ru-RU-u-kf-upper");
    });

    const detailedTableActive = buildDetailedTableForClients(
        clientsSortedByName.filter((clientEntry) => !archivedByClientId[clientEntry.id])
    );

    const detailedTableArchived = buildDetailedTableForClients(
        clientsSortedByName.filter((clientEntry) => archivedByClientId[clientEntry.id])
    );

    const summaryDetailedTable = buildDetailedTableForClients(clientsSortedByName);

    return {
        summaryDetailedTable,
        detailedTableActive,
        detailedTableArchived,
        monthKeysDesc,
        tables: compressed.tables,
        clientDirectory,
    };
};

export const handlePrintResponse = (
    clientId: number | string,
    callbackOnSuccess: any,
    callbackOnFail: any
) => {
    Api.get("crm-services", `print?data[client_id]=${clientId}`).all((response) => {
        if (response.result === "success") {
            // @ts-ignore // @fixme косяк сборщика
            const filePath = process.env.REACT_APP_API_URL + response.meta.path;
            const link = document.createElement("a");
            link.href = filePath;
            link.download = filePath.split("/").pop();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            callbackOnSuccess(response);
        } else {
            console.error(
                "Ошибка при генерации файла:",
                response.meta.message || "Неизвестная ошибка"
            );
            callbackOnFail(response);
        }
    });
};

export const printAtonExcelFile = (clientId: any) =>
    Api.get("crm-services", `print?data[client_id]=${clientId}`).all((response) => {
        if (response.result === "success") {
            downloadExcelFile(response, true);
        } else {
            console.error(
                "Ошибка при генерации файла:",
                response.meta.message || "Неизвестная ошибка"
            );
        }
    });

export const getServicesOnConfirmationWindow = (
    clientId: string | number,
    callbackOnSuccess?: any
) => {
    return Api.get("crm-services", "index")
        .with("clients", (query: any) => {
            query.orderBy("created_at");
            return query.where("client_id", "=", clientId);
        })
        .whereHas("clients", (query: any) => {
            return query.where("client_id", "=", clientId);
        })
        .where("is_archived", "=", false)
        .all((response) => {
            callbackOnSuccess && callbackOnSuccess(response);
            // setServices(res.data);
        });
};
